import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { BACKEND_CONFIG } from '../config/backendConfig';
import { CheckCircle, AlertTriangle, Mail, Smartphone, ExternalLink } from 'react-feather';
import { useResponsive } from '../hooks/useResponsive';
import { COACH_APP_OPEN, COACH_SETUP_PATH } from '../utils/coachEntry';

// Mobile UA detection. Drives the "Open in app" CTA — we only want to surface
// it to users on a phone, since the deep link does nothing on desktop and
// muddies the verify-success UX there. iPad ships intentionally because the
// app supports it; desktop Safari spoofing iPad is rare enough to live with.
const isMobileUA = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

// Deep-link scheme registered in mobile/ios/Info.plist + AndroidManifest.xml.
// Same scheme staging + prod (the session token in the URL is what binds the
// user to the right backend — a staging-minted session won't work against
// prod's session store and vice versa).
const APP_SCHEME = 'apsrecruits';

const EmailVerifiedPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithRedirect, user: auth0User, isLoading: auth0IsLoading } = useAuth0();
  const { isAuthenticated, isLoading: authLoading, setSessionDirectly } = useAuth();
  const toast = useToast();

  const success = searchParams.get('success') !== 'false';
  const message = searchParams.get('message') || '';
  // Canonical path now: GET /api/auth/verify-email/:token (our backend) has
  // already validated the JWT, marked Auth0 email_verified=true, and minted
  // a backend session — it then 302'd to /email-verified?success=true&session=...
  // We just install the session and bounce into onboarding.
  const sessionFromUrl = searchParams.get('session') || '';
  const email = searchParams.get('email') || auth0User?.email || '';

  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [directLoginFailed, setDirectLoginFailed] = useState(false);
  // Set true after the user taps "Open in app" and ~1.5s passes without the
  // page losing focus — strong signal the scheme isn't registered (app not
  // installed). Surfaces a "Don't have the app yet?" prompt to nudge them
  // back to the in-browser flow.
  const [appOpenAttemptFailed, setAppOpenAttemptFailed] = useState(false);
  // Ref (not state) so toggling it doesn't re-run the effect.
  const firedRef = useRef(false);
  // Mobile UA is decided once at mount — server-side render protection plus
  // we don't want a window resize to flip the UI mid-page.
  const isMobilePlatform = useMemo(() => isMobileUA(), []);
  // Only offer the "Open in app" handoff when we actually have a session token
  // to hand the app. Without this guard, a mobile user who lands here with no
  // ?session= (e.g. a legacy/Auth0-default verify link, or the failure
  // redirect) would see a disabled "Open in app" button. When there's no
  // session, fall through to the normal in-browser path (auto-login or the
  // Continue fallback) exactly like desktop.
  // A coach sign-up (intent=coach from the verify link) goes to coach setup.
  const coachSignup = searchParams.get('intent') === 'coach';
  const showAppHandoff = isMobilePlatform && !!sessionFromUrl && (!coachSignup || COACH_APP_OPEN);

  // Phase 1 D3: auto-login by minting a backend session directly from the
  // just-verified Auth0 user, instead of bouncing through Universal Login.
  //
  // Previously this page called loginWithRedirect() which — even with no
  // `prompt` set — landed verified users back on a password form. The new
  // flow:
  //   1. POST /auth/post-verify-session with { email }
  //   2. Backend confirms email_verified=true + fresh updated_at on Auth0 user
  //   3. Backend mints a session, returns sessionToken
  //   4. setSessionDirectly() installs it (no Auth0 round-trip)
  //   5. Navigate to /onboarding/quickStart
  //
  // If the direct-session endpoint fails (window expired, network error),
  // fall back to the manual "Continue" button which still uses
  // loginWithRedirect — that way the user is never stranded.
  // Stable refs so the effect can fire-and-forget without depending on
  // identity-unstable values from the auth provider.
  const setSessionDirectlyRef = useRef(setSessionDirectly);
  const navigateRef = useRef(navigate);
  useEffect(() => { setSessionDirectlyRef.current = setSessionDirectly; }, [setSessionDirectly]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  // Defensive: if we have NO session token in URL AND no email anywhere
  // after Auth0 SDK has settled, neither path can fire. Surface the
  // manual Continue fallback so the user can still get through.
  useEffect(() => {
    if (success && !auth0IsLoading && !authLoading && !isAuthenticated && !sessionFromUrl && !email) {
      setDirectLoginFailed(true);
    }
  }, [success, auth0IsLoading, authLoading, isAuthenticated, sessionFromUrl, email]);

  // PRIMARY PATH (new): backend has already minted a session and embedded
  // it in the redirect as ?session=<token>. Install it immediately, no
  // network call needed.
  //
  // Mobile UA opts OUT of auto-install — we hold the page so the user can
  // pick between "Open in app" (deep link) and "Continue in browser". Auto-
  // navigating to /onboarding/quickStart on a phone defeats the whole point
  // of giving them the chance to land in the app instead.
  useEffect(() => {
    if (showAppHandoff) return;
    if (!success || isAuthenticated || !sessionFromUrl) return;
    if (firedRef.current) return;
    firedRef.current = true;
    (async () => {
      try {
        console.log('[email-verified] installing session from URL');
        await setSessionDirectlyRef.current(sessionFromUrl);
        navigateRef.current(coachSignup ? COACH_SETUP_PATH : '/onboarding/quickStart', { replace: true });
      } catch (err) {
        console.error('Failed to install session from URL:', err);
        setDirectLoginFailed(true);
      }
    })();
  }, [success, isAuthenticated, sessionFromUrl, showAppHandoff]);

  // LEGACY PATH: pre-verify-email-link signups (or prod, once we're shared).
  // Still call /auth/post-verify-session by email if we have one but no
  // session token in the URL. Kept as a safety net for any in-flight
  // verifications that started before this deploy.
  //
  // Same mobile opt-out as the primary path — give the user the choice.
  useEffect(() => {
    if (showAppHandoff) return;
    if (!success || isAuthenticated || !email || sessionFromUrl) return;
    if (firedRef.current) return;
    // Deliberately NOT gating on authLoading. Auth0's getAccessTokenSilently
    // can hang for seconds (or forever) when the browser holds a stale SSO
    // cookie for a user that's since been deleted — that leaves AuthContext
    // stuck in isLoading=true and used to wedge this page on "Redirecting
    // you now…". The post-verify-session call is independent of Auth0; it
    // only needs the email from the URL, so we race ahead.
    //
    // Flip firedRef BEFORE awaiting anything — guards against re-entry from
    // re-renders, and the fetch is already in flight so a cleanup cancellation
    // can't strand us anymore.
    firedRef.current = true;

    (async () => {
      try {
        console.log('[email-verified] POST /auth/post-verify-session', email);
        const res = await fetch(`${BACKEND_CONFIG.baseURL}/auth/post-verify-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (!res.ok) {
          const errText = await res.text().catch(() => '');
          throw new Error(`post-verify-session ${res.status} ${errText}`);
        }
        const body = await res.json();
        if (!body?.sessionToken) {
          throw new Error('no sessionToken in response');
        }
        await setSessionDirectlyRef.current(body.sessionToken);
        navigateRef.current(coachSignup ? COACH_SETUP_PATH : '/onboarding/quickStart', { replace: true });
      } catch (err) {
        console.error('Direct post-verify login failed, showing manual fallback:', err);
        setDirectLoginFailed(true);
        // Allow a manual retry via Continue button — keep firedRef true so the
        // effect doesn't loop, but the user has the fallback.
      }
    })();
  }, [success, isAuthenticated, email, showAppHandoff, sessionFromUrl]);

  // Mobile "Open in app" CTA. Fires the apsrecruits:// scheme. If the scheme
  // isn't registered (app not installed) the page stays put — we use the
  // 1.5s focus check to detect that and surface a "Don't have the app?"
  // prompt that nudges the user back into the browser flow.
  //
  // Historical note: this used to wrap the deep link in a hidden iframe to
  // avoid Safari's "address is invalid" dialog when the scheme is missing.
  // The iframe approach silently no-ops on Safari iOS (confirmed against a
  // simulator where `xcrun simctl openurl apsrecruits://...` worked but the
  // page-level iframe did not), so we go back to the canonical
  // window.location.href. On iOS the OS intercepts the scheme before Safari
  // shows any error; on Android Chrome the intent handler picks it up.
  const handleOpenInApp = () => {
    if (!sessionFromUrl) return;
    const target = `${APP_SCHEME}://verify-success?session=${encodeURIComponent(sessionFromUrl)}&email=${encodeURIComponent(email)}`;
    setAppOpenAttemptFailed(false);
    window.location.href = target;
    // If we're still focused after 1.5s, the scheme didn't resolve (no app
    // installed, or the user dismissed the iOS "Open in 'APS Recruits'?"
    // prompt). Surface the in-browser fallback prominently.
    setTimeout(() => {
      if (typeof document !== 'undefined' && document.hasFocus && document.hasFocus()) {
        setAppOpenAttemptFailed(true);
      }
    }, 1500);
  };

  // Mobile "Continue in browser" — install the session and route into
  // onboarding the same way the desktop auto-effect does. Used when the user
  // explicitly opts into the web flow OR when the deep link silently fails.
  const handleContinueInBrowser = async () => {
    if (!sessionFromUrl) {
      // No session token (legacy path) — fall back to Auth0 universal login.
      try {
        sessionStorage.setItem('postLoginRedirect', '/onboarding/quickStart');
      } catch {}
      loginWithRedirect({
        authorizationParams: { login_hint: email || undefined },
      });
      return;
    }
    try {
      await setSessionDirectlyRef.current(sessionFromUrl);
      navigateRef.current('/onboarding/quickStart', { replace: true });
    } catch (err) {
      console.error('[email-verified] in-browser continue failed:', err);
      setDirectLoginFailed(true);
    }
  };

  const handleResendVerification = async () => {
    if (!email) return;
    setResendingEmail(true);
    setResendSuccess(false);
    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setResendSuccess(true);
      } else {
        // Previously silent on a non-OK response — the button just reset with
        // no feedback. Surface the failure so the user knows to retry.
        toast.error("We couldn't resend the verification email. Please try again.");
      }
    } catch (err) {
      console.error('Failed to resend verification email:', err);
      toast.error("We couldn't resend the verification email. Please try again.");
    }
    setResendingEmail(false);
  };

  // Already signed-in users skip this screen.
  // Target updated for Phase 1 D3 — ProtectedRoute will redirect past
  // /onboarding/quickStart to /dashboard if onboarding_step is already 100.
  if (isAuthenticated) {
    return <Navigate to="/onboarding/quickStart" replace />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0f0b24',
      fontFamily: 'Manrope, sans-serif',
      position: 'relative',
      overflow: 'hidden',
      padding: '40px 20px'
    }}>
      {/* Animated mesh gradient background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(249, 85, 13, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 40% 60%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
        `,
        opacity: 0.6
      }} />

      {/* Animated orbs */}
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(249, 85, 13, 0.2) 0%, transparent 60%)',
        top: '-150px',
        right: '-100px',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 8s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 60%)',
        bottom: '-100px',
        left: '-50px',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 10s ease-in-out infinite reverse'
      }} />

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) scale(1); }
            50% { transform: translateY(-30px) scale(1.05); }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse-success {
            0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
            50% { box-shadow: 0 0 0 20px rgba(16, 185, 129, 0); }
          }
          .success-pulse { animation: pulse-success 2s ease-in-out infinite; }
        `}
      </style>

      {/* Logo Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '32px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            overflow: 'hidden',
            background: 'white',
            padding: '6px',
            flexShrink: 0
          }}>
            <img
              src="/images/logo-180.png"
              alt="APS Recruits Logo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? '24px' : '28px',
            fontWeight: '800',
            letterSpacing: '-0.5px',
            color: 'white'
          }}>
            APS Recruits
          </h1>
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: isMobile ? '32px 24px' : '48px',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        {success ? (
          <>
            <div
              className="success-pulse"
              style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
              }}
            >
              <CheckCircle size={40} color="white" strokeWidth={2.5} />
            </div>

            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#151133',
              marginBottom: '12px'
            }}>
              Email Verified!
            </h2>

            {email && (
              <p style={{
                fontSize: '16px',
                color: '#f9550d',
                fontWeight: '600',
                marginBottom: '12px'
              }}>
                {email}
              </p>
            )}

            <p style={{
              fontSize: '15px',
              color: '#64748b',
              lineHeight: '1.6',
              marginBottom: '28px'
            }}>
              {showAppHandoff
                ? 'Your account is ready. Open it in the app to finish setup.'
                : "Your account is all set. We're signing you in and taking you to set up your family profile."}
            </p>

            {showAppHandoff ? (
              <>
                {/* Mobile: "Open in app" is the primary CTA. The deep link
                    fires apsrecruits://verify-success?session=...&email=... .
                    If the app isn't installed, handleOpenInApp's 1.5s focus
                    check flips appOpenAttemptFailed and we surface the
                    "Continue in browser" fallback prominently. */}
                <button
                  onClick={handleOpenInApp}
                  disabled={!sessionFromUrl}
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: sessionFromUrl
                      ? 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)'
                      : '#fdba74',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: sessionFromUrl ? 'pointer' : 'not-allowed',
                    fontFamily: 'Manrope, sans-serif',
                    boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                  }}
                >
                  <Smartphone size={18} />
                  Open in APS Recruits app
                </button>

                {appOpenAttemptFailed && (
                  <div style={{
                    padding: '12px',
                    background: '#fef3c7',
                    border: '1px solid #fcd34d',
                    borderRadius: '12px',
                    color: '#92400e',
                    fontSize: '13px',
                    fontWeight: '500',
                    marginBottom: '12px',
                    textAlign: 'left',
                  }}>
                    Don't have the app installed? Continue in your browser
                    below. You can get the app any time.
                  </div>
                )}

                <button
                  onClick={handleContinueInBrowser}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    background: 'white',
                    color: '#151133',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <ExternalLink size={16} />
                  Continue in browser
                </button>
              </>
            ) : (
              <>
                {/* Desktop: existing spinner + manual fallback button (used to
                    be the only path; kept as-is for desktop users). */}
                {!directLoginFailed && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(249, 85, 13, 0.2)',
                    borderTop: '2px solid #f9550d',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <span style={{
                    fontSize: '14px',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>
                    Redirecting you now…
                  </span>
                </div>
                )}

                {directLoginFailed && (
                  <div style={{
                    padding: '12px',
                    background: '#fef3c7',
                    border: '1px solid #fcd34d',
                    borderRadius: '12px',
                    color: '#92400e',
                    fontSize: '13px',
                    fontWeight: '500',
                    marginBottom: '12px',
                    textAlign: 'left'
                  }}>
                    Couldn't sign you in automatically. Tap Continue to finish signing in.
                  </div>
                )}

                <button
                  onClick={() => {
                    try {
                      sessionStorage.setItem('postLoginRedirect', '/onboarding/quickStart');
                    } catch {}
                    loginWithRedirect({
                      authorizationParams: {
                        login_hint: email || undefined,
                      },
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif',
                    boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 85, 13, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.3)';
                  }}
                >
                  Continue
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)'
            }}>
              <AlertTriangle size={36} color="white" />
            </div>

            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#151133',
              marginBottom: '12px'
            }}>
              Verification Failed
            </h2>

            <p style={{
              fontSize: '15px',
              color: '#64748b',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              {message || "We couldn't verify your email. The link may have expired or already been used."}
            </p>

            {email && (
              <button
                onClick={handleResendVerification}
                disabled={resendingEmail}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: resendingEmail
                    ? '#fdba74'
                    : 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: resendingEmail ? 'not-allowed' : 'pointer',
                  fontFamily: 'Manrope, sans-serif',
                  boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
                  transition: 'all 0.2s ease',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {resendingEmail ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Sending…
                  </>
                ) : (
                  <>
                    <Mail size={18} />
                    {resendSuccess ? 'Resend again' : 'Resend verification email'}
                  </>
                )}
              </button>
            )}

            {resendSuccess && (
              <div style={{
                padding: '14px',
                background: '#d1fae5',
                border: '1px solid #6ee7b7',
                borderRadius: '12px',
                color: '#065f46',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '12px'
              }}>
                Verification email sent. Check your inbox.
              </div>
            )}

            <a
              href="/login"
              style={{
                display: 'block',
                width: '100%',
                padding: '14px',
                fontSize: '15px',
                fontWeight: '600',
                background: 'white',
                color: '#151133',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif',
                textDecoration: 'none',
                textAlign: 'center',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease'
              }}
            >
              Back to sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerifiedPage;
